import React from 'react';

import type ReserveInfo from '@this/domain/reserve_info';
import { Text as AITText } from '@this/components/shared/ui/data_displays/typography';
import { useShortDistance, SHORT_DISTANCE_MODE } from '@this/src/lib/hooks/useShortDistance';
import { digits } from '@this/src/util';
import type User from '@this/domain/user/user';
import BufferAmountSetting from '@this/domain/organization/buffer_amount_setting';
import Tooltip from '@this/shared/tooltip/tooltip';
import BufferAmountsSection from '@this/components/reserve_trip/reserve_confirm/outline/buffer_amounts_section';
import moment from '../../../../lib/moment';

import ReservingTrip from '../../../../domain/trip/reserving_trip';

import Text from '../../../shared/text/text';
import ReserveConfirmOutlineItemTemplate from '../outline_item/outline_item.template';
import ReserveConfirmOutlineItemShortdistanceTemplate from '../outline_item/outline_item_shortdistance.template';

import ReserveConfirmOutlineDetailTemplate from '../outline_detail/outline_detail.template';

type Props = {
  userInfo: User;
  reserveInfo: ReserveInfo;
  peoplenum: number;
  roomnum: number | undefined;
  // queries: { stay_days: number };
  // shinkansenTooLate: boolean;
};

const ReserveConfirmOutlineTemplate: React.SFC<Props> = ({
  userInfo,
  reserveInfo,
  peoplenum,
  roomnum
  // queries
  // shinkansenTooLate
}) => {
  const reservingTrip = reserveInfo.reservingTrip;
  const travelers = reserveInfo.travelers;
  const { shortDistanceMode } = useShortDistance();
  const styles = getStyles();
  const bufferAmountSetting = new BufferAmountSetting(userInfo.organization);

  return (
    <div className="reserve-confirm__outline" id="right-navi-content" data-testid="rc-summary-root">
      <div style={styles.rightSubTitle} data-testid="rc-summary-title">
        予約内容の概要
      </div>
      <div className="reserve-confirm-right-wrapper">
        <div style={styles.rightSection}>
          <AITText color="danger">
            下記が最終的なご依頼内容となります。日時や金額等、間違いがないかご確認ください。
          </AITText>
        </div>
        <div style={styles.rightSection} data-testid="rc-summary-section-schedule">
          <div className="reserve-confirm-right-title" data-testid="rc-summary-schedule-title">
            日程
          </div>
          <div className="reserve-confirm-right-content" data-testid="rc-summary-schedule-content">
            <p data-wovn-ignore>
              {moment(reservingTrip.startDate()).format('M月D日')}〜
              {moment(reservingTrip.endDate()).format('M月D日')}
            </p>
          </div>
        </div>
        {reservingTrip.items && reservingTrip.items.length > 0 && (
          <>
            <div style={styles.rightSection} data-testid="rc-summary-section-total-price">
              <div style={styles.totalPriceBox} data-testid="rc-summary-total-price-box">
                当サイトで予約可能な合計金額
                <div style={styles.totalPriceBoxPrice} data-wovn-ignore data-testid="rc-summary-total-price">
                  {digits(reservingTrip.totalPrice())}円
                </div>
              </div>
              {reservingTrip.itemsTotalBufferAmount(bufferAmountSetting) > 0 && (
                <div style={styles.totalPriceBox} data-testid="rc-summary-buffer-price-box">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>値上がり許容幅込の申請金額</span>
                    <Tooltip type="info">
                      <div className="select__tooltip-format">
                        承認後、実際に商品を手配する前に値上がりが発生した場合、いくらまで値上がりを許容して手配するかを管理画面で設定いただいています。
                      </div>
                    </Tooltip>
                  </div>
                  <div>{utils.digits(reservingTrip.totalPriceWithBuffer(bufferAmountSetting))}円</div>
                </div>
              )}
            </div>
            {reservingTrip.hasLegSummaries() && (
              <div style={styles.rightSection}>
                <div className="reserve-confirm-right-title">【この旅程にかかる金額詳細】</div>
                <div className="reserve-confirm-right-content-note">
                  ※AIトラベル以外の提示している金額はあくまで目安です。
                </div>
              </div>
            )}
            {reservingTrip.itemsTotalBufferAmount(bufferAmountSetting) > 0 && (
              <BufferAmountsSection reservingTrip={reservingTrip} bufferAmountSetting={bufferAmountSetting} />
            )}
            <div style={styles.outlineBox}>
              <div style={styles.outlineBoxTitle}>当サイトで予約可能な商品の詳細</div>
              {reservingTrip.items.map(
                (item, i) =>
                  item && (
                    <ReserveConfirmOutlineItemTemplate
                      isPackage={reservingTrip.isPackage()}
                      key={i}
                      domesticAirPriceIndex={item.domesticAirPriceIndex}
                      element={item.element}
                      elementType={item.element_type}
                      elementRaw={item.element_raw}
                      foreignExist={item.foreignExist}
                      // shinkansenTooLate={shinkansenTooLate}
                      flightDescription={item.flightDescription()}
                      minirule={item.minirule()}
                      deadline={item.getDeadlineHour()}
                      flightPrice={item.totalPrice() || 0}
                      hotelPrice={item.totalPrice() || 0}
                      peoplenum={peoplenum}
                      roomnum={roomnum}
                      travelers={travelers}
                      index={item.index}
                    />
                  )
              )}
              <hr style={styles.outlineBoxHr} />
              <div style={styles.rightContentTotal}>
                {reservingTrip.isPackage() ? (
                  <div style={styles.totalPrice} data-wovn-ignore>
                    <Text data-wovn-ignore text={reservingTrip.tooltipText()} />
                  </div>
                ) : (
                  <div style={styles.totalPrice} data-wovn-ignore>
                    {digits(reservingTrip.totalPrice())}円(税込)
                  </div>
                )}
              </div>
            </div>
            {reservingTrip.hasLegSummaries() && shortDistanceMode === SHORT_DISTANCE_MODE.ALL && (
              <>
                <div style={styles.outlineBox}>
                  <div style={styles.outlineBoxTitle}>その他経費目安</div>
                  {reservingTrip.items.map(
                    item =>
                      item && (
                        <ReserveConfirmOutlineItemShortdistanceTemplate
                          element={item.element}
                          elementType={item.element_type}
                        />
                      )
                  )}
                  <hr style={styles.outlineBoxHr} />
                  <div style={styles.rightContentTotal}>
                    <div style={styles.totalPrice} data-wovn-ignore>
                      {digits(reservingTrip.totalPriceShortDistance())}円(税込)
                    </div>
                  </div>
                </div>
                <div style={styles.outlineBoxAllTotal}>
                  <div style={styles.outlineBoxTitleAllTotal}>この旅程でかかる費用目安</div>
                  <div style={styles.totalPriceAllTotal} data-wovn-ignore>
                    {digits(reservingTrip.totalPrice() + reservingTrip.totalPriceShortDistance())}円(税込)
                  </div>
                </div>
              </>
            )}
            {reservingTrip.isAirPackage() && (
              <div style={styles.rightSection}>
                <div style={styles.rightTitle}>■旅行企画・実施</div>
                <div style={styles.rightContent}>
                  <Text
                    text={
                      reservingTrip.isAnaAirPackage()
                        ? ReservingTrip.anaPackageDesc()
                        : reservingTrip.isJalAirPackage()
                        ? ReservingTrip.jalPackageDesc()
                        : ''
                    }
                  />
                </div>
                <div style={styles.rightTitle}>■受託販売会社</div>
                <div style={styles.rightContent}>
                  <Text text={ReservingTrip.consignmentSaleDesc(userInfo.service.consignment_sale_tel)} />
                </div>
              </div>
            )}
            <br />
            <div style={styles.rightSection}>
              <div className="reserve-confirm-right-title">経路詳細</div>
              <ReserveConfirmOutlineDetailTemplate reservingTrip={reservingTrip} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function getStyles() {
  return {
    rightSubTitle: {
      fontSize: '18px',
      fontWeight: 600,
      marginBottom: '10px'
    } as React.CSSProperties,
    rightSection: {
      marginBottom: '15px'
    } as React.CSSProperties,
    rightTitle: {
      fontWeight: 'bold'
    } as React.CSSProperties,
    rightContent: {
      marginLeft: '10px'
    } as React.CSSProperties,
    rightContentTotal: {
      textAlign: 'right',
      padding: '5px'
    } as React.CSSProperties,
    totalPrice: {
      fontSize: '13px',
      fontWeight: 'bold'
    } as React.CSSProperties,
    totalPriceBox: {
      backgroundColor: '#feffd2',
      padding: '10px',
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    } as React.CSSProperties,
    totalPriceBoxPrice: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#dc2626'
    } as React.CSSProperties,
    outlineBox: {
      border: '1px solid #d1d5db',
      marginTop: '-1px'
    } as React.CSSProperties,
    outlineBoxTitle: {
      padding: '10px',
      fontWeight: 'bold',
      backgroundColor: '#f9fafb'
    } as React.CSSProperties,
    outlineBoxHr: {
      margin: '4px 0'
    } as React.CSSProperties,
    outlineBoxAllTotal: {
      border: '1px solid #d1d5db',
      marginTop: '-1px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    } as React.CSSProperties,
    outlineBoxTitleAllTotal: {
      padding: '10px',
      fontWeight: 'bold',
      fontSize: '13px',
      backgroundColor: '#e5e7eb',
      width: '55%'
    } as React.CSSProperties,
    totalPriceAllTotal: {
      padding: '5px',
      fontSize: '16px',
      fontWeight: 'bold'
    } as React.CSSProperties
  } as const;
}

export default ReserveConfirmOutlineTemplate;
